import React, { CSSProperties, useCallback, useEffect, useMemo, useState } from 'react';
import {
  configurationSelectOptions,
  rtkQueryPatchStatusToConfigurationFieldStatus,
  skuColumnBackgroundColors,
  SkuTypeColumn,
} from '@ydistri/utils';
import {
  ConfigurationDataType,
  ConfigurationFieldType,
  FieldConfigurationDeleteRequest,
  FieldConfigurationRequest,
  SkuType,
} from '@ydistri/api-sdk';
import { ConfigurationInput, ConfigurationSelect, ConfigurationSwitch } from '@ydistri/ds';
import useCategoryId from '../../../hooks/useCategoryId';
import { useCategoryConfiguration } from '../../../hooks/useCategoryConfiguration';
import { usePatchCategoryConfigurationMutation } from '../../../screens/Configuration/apiTemplate';
import { useDeleteCategoryConfigurationMutation } from '../CategoryTree/apiCategoryTree';
import { Popover } from 'antd';
import { css, styled } from 'styled-components';
import { getConfigurationFieldHoverData } from './getConfigurationFieldHoverData';
import { createPayload } from './createPayload';
import ConfigurationSelectRequestList from '../../../screens/Configuration/ConfigurationCategories/SegmentPotentialDefinition/ConfigurationSelectRequestList';
import { CheckBoxFieldProps, OtherTypesFieldProps } from './ConfigurationFieldTypes';
import { useSelectedTemplateId } from '../../../hooks/useSelectedTemplateId';
import { computeRemSize, STYLE } from '@ydistri/theme';

interface ConfigurationFieldWrapperProps {
  $skuTypeColumn: SkuTypeColumn;
  $inputWidth?: CSSProperties['width'];
  $padding?: CSSProperties['padding'];
  $noAdditionalSpace?: boolean;
  $autoHeight?: boolean;
}

export const ConfigurationFieldWrapper = styled.span<ConfigurationFieldWrapperProps>`
  display: flex;
  align-items: center;
  padding: ${({ $padding }) => $padding ?? '0.25rem 0.25rem 0.25rem 0.75rem'};
  ${p => !p.$autoHeight && `min-height: ${computeRemSize(37)};`}
  height: 100%;

  ${({ $noAdditionalSpace, $inputWidth }) => {
    if (!$noAdditionalSpace) {
      return css`
        min-width: calc(${$inputWidth ?? STYLE.DEFAULT_INPUT_WIDTH} + 1.5rem);
      `;
    }
  }}

  ${({ $skuTypeColumn }) =>
    $skuTypeColumn !== null &&
    css`
      background-color: ${skuColumnBackgroundColors[$skuTypeColumn]};
    `}
`;

export interface ConfigurationFieldProps {
  disabled?: boolean;
  skuTypeColumn: SkuTypeColumn;
  fieldType: ConfigurationFieldType;
  dataType: ConfigurationDataType;
  inputWidth?: CSSProperties['width'];
  showValueAsText?: boolean;
  padding?: CSSProperties['padding'];
  noAdditionalSpace?: boolean;
  forcedCategoryId?: number;
  autoHeight?: boolean;
  onBeforeSave?: (categoryId: number, payload: FieldConfigurationRequest[]) => void;
  onAfterSave?: (categoryId: number, payload: FieldConfigurationRequest[]) => void;
}

const ConfigurationField: React.FC<CheckBoxFieldProps | OtherTypesFieldProps> = ({
  disabled = false,
  skuTypeColumn,
  fieldType,
  dataType,
  inputWidth,
  showValueAsText = false,
  padding,
  noAdditionalSpace,
  forcedCategoryId,
  autoHeight,
  onBeforeSave,
  onAfterSave,
  typeConfiguration,
}) => {
  const selectedTemplateId = useSelectedTemplateId();
  const selectedCategoryId = useCategoryId();
  const categoryId = forcedCategoryId ?? selectedCategoryId;
  const { rootCategoryConfiguration, categoryConfigurationData: configurations } =
    useCategoryConfiguration(categoryId);

  const [hovered, setHovered] = useState(false);

  const [patchConfiguration, patchConfigurationStatus] = usePatchCategoryConfigurationMutation();
  const [deleteConfiguration, { isLoading: clearing }] = useDeleteCategoryConfigurationMutation();

  const status = useMemo(
    () => rtkQueryPatchStatusToConfigurationFieldStatus(patchConfigurationStatus),
    [patchConfigurationStatus],
  );

  useEffect(() => {
    patchConfigurationStatus.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- we need to reset the status on every categoryId change
  }, [categoryId]);

  const updateConfigHandler = useCallback(
    (value: number | string) => {
      if (selectedTemplateId === undefined) {
        return;
      }

      const basePayload: FieldConfigurationRequest = {
        configurationFieldType: fieldType,
        value: typeof value === 'number' ? value.toString() : value,
        skuType: undefined,
      };

      const payload: FieldConfigurationRequest[] = createPayload(basePayload, skuTypeColumn);

      if (onBeforeSave) {
        onBeforeSave(categoryId, payload);
      }
      patchConfiguration({
        templateId: selectedTemplateId,
        categoryId,
        payload,
      }).finally(() => {
        if (onAfterSave) {
          onAfterSave(categoryId, payload);
        }
      });
    },
    [
      fieldType,
      skuTypeColumn,
      onBeforeSave,
      patchConfiguration,
      selectedTemplateId,
      categoryId,
      onAfterSave,
    ],
  );

  const clearExceptionHandler = useCallback(() => {
    if (selectedTemplateId === undefined) {
      return;
    }

    const basePayload: FieldConfigurationDeleteRequest = {
      configurationFieldType: fieldType,
      skuType: null,
    };

    const payload: FieldConfigurationDeleteRequest[] = createPayload(basePayload, skuTypeColumn);

    deleteConfiguration({
      templateId: selectedTemplateId,
      categoryId,
      fields: payload,
    });

    setHovered(false);
  }, [fieldType, skuTypeColumn, deleteConfiguration, selectedTemplateId, categoryId]);

  const validSkuType: SkuType = useMemo(
    () =>
      skuTypeColumn === null || skuTypeColumn === 'Target' ? SkuType.FastMover : skuTypeColumn,
    [skuTypeColumn],
  );

  const configuration = useMemo(
    () =>
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- may be null
      configurations?.[fieldType]?.[validSkuType] ??
      (dataType !== ConfigurationDataType.SelectBoxRequestList
        ? { value: '100', overwritten: false, exceptions: [] } //TODO 4756: this will not be needed
        : undefined),
    [configurations, dataType, fieldType, validSkuType],
  );

  const exceptions = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- may be null
    return configurations?.[fieldType]?.[validSkuType]?.exceptions ?? [];
  }, [configurations, fieldType, validSkuType]);

  const isHidden = useMemo(() => {
    let hidden = false;
    if (fieldType === ConfigurationFieldType.PsMaxForecastConfidence) {
      hidden =
        !configurations?.[ConfigurationFieldType.PsMonthsOfSupplyToKeep][validSkuType]?.value;
    } else if (
      fieldType === ConfigurationFieldType.PtMinForecastConfidence ||
      fieldType === ConfigurationFieldType.PtMinimal6MonthsFrequencyOfSales
    ) {
      hidden =
        !configurations?.[ConfigurationFieldType.PtMonthsOfSupplyToGetByForecast][validSkuType]
          ?.value;
    }
    return hidden;
  }, [configurations, fieldType, validSkuType]);

  const handleHoverChange = useCallback((open: boolean) => {
    setHovered(open);
  }, []);

  const hoverData = useMemo(
    () =>
      getConfigurationFieldHoverData({
        selectedCategoryId: categoryId,
        exceptions,
        rootCategoryConfiguration,
        fieldType,
        dataType,
        validSkuType,
        clearing,
        disabled,
        clearExceptionHandler,
      }),
    [
      categoryId,
      exceptions,
      rootCategoryConfiguration,
      fieldType,
      dataType,
      validSkuType,
      clearing,
      disabled,
      clearExceptionHandler,
    ],
  );

  const isRequestList = dataType === ConfigurationDataType.SelectBoxRequestList;

  const input = useMemo(() => {
    if (!configuration) {
      if (dataType === ConfigurationDataType.SelectBoxRequestList) {
        return <ConfigurationSelectRequestList width={inputWidth} disabled={disabled} />;
      }
    }

    if (isHidden) return '-';
    if (!configuration) return '';
    if (showValueAsText) return configuration.value;

    switch (dataType) {
      case ConfigurationDataType.SelectBoxTargetList:
      case ConfigurationDataType.SelectBoxSourceList:
      case ConfigurationDataType.SelectBoxForcedList:
      case ConfigurationDataType.SelectBoxMinLayerList:
      case ConfigurationDataType.HfmqPairs:
        return null; //special case, not here
      case ConfigurationDataType.SelectBoxRequestList:
        return null; //returned above
      case ConfigurationDataType.CheckBox:
        return (
          <ConfigurationSwitch
            status={status}
            overwritten={configuration.overwritten}
            parentException={exceptions.length > 0}
            value={parseInt(configuration.value) === 1}
            disabled={disabled}
            onChange={updateConfigHandler}
            {...typeConfiguration}
          />
        );
      case ConfigurationDataType.SelectBoxForecastConfidence:
        return (
          <ConfigurationSelect<number>
            status={status}
            overwritten={configuration.overwritten}
            parentException={exceptions.length > 0}
            options={
              disabled
                ? configurationSelectOptions.historicForecastConfidence
                : configurationSelectOptions[dataType]
            }
            disabled={disabled}
            value={parseInt(configuration.value)}
            onChange={updateConfigHandler}
            width={inputWidth}
          />
        );
      case ConfigurationDataType.SelectBoxMonthIdentifySkuType:
        return (
          <ConfigurationSelect
            status={status}
            overwritten={configuration.overwritten}
            parentException={exceptions.length > 0}
            options={configurationSelectOptions[dataType]}
            disabled={disabled}
            value={configuration.value}
            onChange={updateConfigHandler}
            width={inputWidth}
          />
        );
      case ConfigurationDataType.SelectBoxMonthsForForecast:
        return (
          <ConfigurationSelect
            status={status}
            overwritten={configuration.overwritten}
            parentException={exceptions.length > 0}
            options={configurationSelectOptions[dataType]}
            disabled={disabled}
            value={configuration.value}
            onChange={updateConfigHandler}
            width={inputWidth}
          />
        );
      case ConfigurationDataType.SelectBoxForcingLevelType:
        return (
          <ConfigurationSelect
            status={status}
            overwritten={configuration.overwritten}
            parentException={exceptions.length > 0}
            options={configurationSelectOptions[dataType]}
            disabled={disabled}
            value={configuration.value}
            onChange={updateConfigHandler}
            width={inputWidth}
          />
        );
      case ConfigurationDataType.InputNumber:
        return (
          <ConfigurationInput
            status={status}
            overwritten={configuration.overwritten}
            parentException={exceptions.length > 0}
            disabled={disabled}
            value={parseInt(configuration.value)}
            onChange={updateConfigHandler}
            width={inputWidth}
          />
        );
      case ConfigurationDataType.SkuScopeExclusion:
      default: {
        return null;
        // throw new Error(`Not implemented ConfigurationDataType case`);
      }
    }
  }, [
    configuration,
    isHidden,
    showValueAsText,
    dataType,
    inputWidth,
    disabled,
    status,
    exceptions.length,
    updateConfigHandler,
    typeConfiguration,
  ]);

  const popoverWidth = useMemo(
    () => ({ width: computeRemSize(500), minWidth: computeRemSize(500) }),
    [],
  );

  return (
    <Popover
      style={popoverWidth}
      content={hoverData}
      trigger="hover"
      placement={skuTypeColumn === 'Target' ? 'topRight' : undefined}
      open={hovered && !isRequestList && !!hoverData}
      onOpenChange={handleHoverChange}
    >
      <ConfigurationFieldWrapper
        $inputWidth={inputWidth}
        $skuTypeColumn={skuTypeColumn}
        $padding={padding}
        $noAdditionalSpace={noAdditionalSpace}
        $autoHeight={autoHeight}
      >
        {input}
      </ConfigurationFieldWrapper>
    </Popover>
  );
};

export default ConfigurationField;
